import React, { useEffect, useState, useCallback } from 'react';
import ReplyForm from './ReplyForm';
import '../styles/ReplyThread.css';
import { getUserIdentity } from '../lib/userUtils';
import { apiGet } from '../lib/apiClient';

const MAX_COLLAPSED_REPLIES = 3;
const AVATAR_COLORS = ['#0a66c2', '#7c3aed', '#dc2626', '#16a34a', '#2563eb', '#f97316'];

function cleanId(raw) {
  if (!raw) return '';
  return String(raw).replace(/[^a-zA-Z0-9]/g, '');
}

function getDisplayName(reply, currentUserId) {
  const alias = reply?.user_alias || reply?.userAlias || reply?.display_name || reply?.displayName;
  if (alias) return alias;
  const replyUserId = reply?.user_id || reply?.userId;
  if (!replyUserId) return 'Anonymous';
  if (currentUserId && replyUserId === currentUserId) return 'You';
  const cleaned = cleanId(replyUserId);
  if (!cleaned) return 'Anonymous';
  return `User ${cleaned.slice(-4).toUpperCase()}`;
}

function getInitials(name) {
  if (!name) return 'A';
  const parts = name.trim().split(/\s+/).slice(0, 2);
  if (!parts.length) return 'A';
  const initials = parts.map((p) => p.charAt(0).toUpperCase()).join('');
  return initials.slice(0, 2);
}

function pickAvatarColor(seed) {
  if (!seed) return AVATAR_COLORS[0];
  const hash = seed.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

function formatRelativeTime(timestamp) {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '';
  const diffMs = Date.now() - date.getTime();
  if (diffMs < 30_000) return 'just now';
  const minute = 60_000;
  const hour = 60 * minute;
  const day = 24 * hour;
  const week = 7 * day;

  if (diffMs < hour) {
    const mins = Math.round(diffMs / minute);
    return `${mins}m ago`;
  }
  if (diffMs < day) {
    const hours = Math.round(diffMs / hour);
    return `${hours}h ago`;
  }
  if (diffMs < week) {
    const days = Math.round(diffMs / day);
    return `${days}d ago`;
  }
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function formatAbsoluteTime(timestamp) {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString();
}

function formatReplyingTo(value) {
  if (!value) return null;
  if (value.startsWith('@')) return value;
  const cleaned = cleanId(value);
  if (!cleaned) return null;
  return `@${cleaned.slice(-6).toLowerCase()}`;
}

export default function ReplyThread({ postId, onReplyCountChange }) {
  const [replies, setReplies] = useState([]);
  const [editingReplyId, setEditingReplyId] = useState(null);
  const [userId, setUserId] = useState(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getUserIdentity().then((id) => { if (!cancelled) setUserId(id); });
    return () => { cancelled = true; };
  }, []);

  const fetchReplies = useCallback(async () => {
    try {
      const { items } = await apiGet(`/replies?postId=${postId}`);
      setReplies(items || []);
    } catch (e) {
      console.error('Error fetching replies:', e.message || e);
    }
  }, [postId]);

  useEffect(() => { fetchReplies(); }, [fetchReplies]);

  useEffect(() => {
    if (replies.length <= MAX_COLLAPSED_REPLIES) {
      setShowAll(true);
    }
  }, [replies.length]);

  const handleNewReply = (reply) => {
    setReplies((prev) => [...prev, reply]);
    setShowAll(true);
  };

  const handleUpdateReply = (updated) => {
    setReplies((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
    setEditingReplyId(null);
  };

  useEffect(() => {
    if (typeof onReplyCountChange === 'function') {
      onReplyCountChange(replies.length);
    }
  }, [onReplyCountChange, replies.length]);

  const shouldCollapse = replies.length > MAX_COLLAPSED_REPLIES;
  const visibleReplies = showAll || !shouldCollapse ? replies : replies.slice(0, MAX_COLLAPSED_REPLIES);
  const hiddenCount = shouldCollapse && !showAll ? replies.length - MAX_COLLAPSED_REPLIES : 0;

  return (
    <div className="reply-thread">
      <div className="reply-thread__header">
        <h4>Replies</h4>
        {replies.length > 0 && (
          <span className="reply-thread__count">{replies.length}</span>
        )}
      </div>

      <div className="reply-thread__list">
        {visibleReplies.map((reply) => {
          const isOwner = (reply.user_id || reply.userId) === userId;
          const isEditing = reply.id === editingReplyId;
          const createdAt = reply.created_at || reply.createdAt;
          if (isEditing) {
            return (
              <div key={reply.id} className="reply-card reply-card--editing">
                <ReplyForm
                  postId={postId}
                  reply={reply}
                  isEditMode
                  hideLabel
                  onUpdate={handleUpdateReply}
                  onCancelEdit={() => setEditingReplyId(null)}
                />
              </div>
            );
          }

          const displayName = getDisplayName(reply, userId);
          const initials = getInitials(displayName);
          const avatarColor = pickAvatarColor(displayName);
          const relativeTime = formatRelativeTime(createdAt);
          const absoluteTime = formatAbsoluteTime(createdAt);
          const replyingTo = formatReplyingTo(
            reply.replying_to || reply.replyingTo || reply.parent_user || reply.parentUser || reply.parent_user_id || reply.parentUserId
          );

          return (
            <div key={reply.id} className="reply-card">
              <div className="reply-avatar" style={{ backgroundColor: avatarColor }}>
                {initials}
              </div>
              <div className="reply-main">
                <div className="reply-head">
                  <span className="reply-author">{displayName}</span>
                  {replyingTo && (
                    <span className="reply-context">Replying to {replyingTo}</span>
                  )}
                  {relativeTime && (
                    <>
                      <span className="reply-separator">•</span>
                      <time className="reply-time" dateTime={createdAt} title={absoluteTime}>
                        {relativeTime}
                      </time>
                    </>
                  )}
                </div>
                <div className="reply-body">
                  <p>{reply.content}</p>
                </div>
                {isOwner && (
                  <div className="reply-actions">
                    <button
                      type="button"
                      className="reply-action"
                      onClick={() => setEditingReplyId(reply.id)}
                    >
                      Edit
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {hiddenCount > 0 && (
        <button
          type="button"
          className="reply-thread__toggle"
          onClick={() => setShowAll(true)}
        >
          View {hiddenCount} more repl{hiddenCount === 1 ? 'y' : 'ies'}
        </button>
      )}

      {showAll && shouldCollapse && (
        <button
          type="button"
          className="reply-thread__toggle reply-thread__toggle--less"
          onClick={() => setShowAll(false)}
        >
          Show fewer replies
        </button>
      )}

      <ReplyForm postId={postId} onNewReply={handleNewReply} />
    </div>
  );
}
